import moment from 'moment';
import { NAME } from './constants';
import {
  referenceDataValidationMap,
  categoryTypePrefixMapping,
  inputObjectDefaults,
  dataExportRegions,
  noPollForStatuses,
  previousDataExportScreenStatuses,
} from './data-constants';
import { DATA_EXPORTS_EARLIEST_DATE } from '../constants';

const STANDARD_ERROR = ' - This Field is Required';
const has = Object.prototype.hasOwnProperty;

export const keyToLocationTypeMapping = (key) => {
  const map = {
    airport: 'airport',
    countries: 'country',
    regions: 'region',
  };
  if (key && has.call(map, key)) {
    return map[key];
  }
  return null;
};

export const isChargedAndReadyState = (status) => {
  const isChargedAndReadyStatuses = ['downloading',
    'download_succeeded',
    'download_failed',
    'charge_succeeded',
    'notifying',
    'notify_succeeded',
    'notify_failed'];
  return isChargedAndReadyStatuses.indexOf(status) !== -1;
};

export const getDataExportsState = globalState =>
  (globalState && globalState[NAME] ? globalState[NAME] : {});

const formatPlacesQuery = ({
  exportType: type,
  departureCheckedSearch: dep,
  arrivalCheckedSearch: arr,
  locationQueries,
}) => {
  const places = {};

  switch (type) {
    case 'departing':
      places.locationType = keyToLocationTypeMapping(dep);
      places.departureType = keyToLocationTypeMapping(dep);
      places.departures = locationQueries[type][dep];
      places.locations = locationQueries[type][dep];

      break;
    case 'arriving':
      places.locationType = keyToLocationTypeMapping(arr);
      places.arrivalType = keyToLocationTypeMapping(arr);
      places.arrivals = locationQueries[type][arr];
      places.locations = locationQueries[type][arr];

      break;

    case 'deparr':
      places.locationType = keyToLocationTypeMapping(arr);
      places.arrivalType = keyToLocationTypeMapping(arr);
      places.arrivals = locationQueries.arriving[arr];
      places.departureType = places.arrivalType;
      places.departures = places.arrivals;
      places.locations = locationQueries.arriving[arr];

      break;

    case 'route':
      places.arrivalType = keyToLocationTypeMapping(arr);
      places.arrivals = locationQueries.arriving[arr];
      places.departureType = keyToLocationTypeMapping(dep);
      places.departures = locationQueries.departing[dep];

      break;

    default:
      return places;
  }

  return places;
};

export const getEnumTypesForExportTypes = (category, exportType) => {
  let typeInProperTense = exportType;
  if (exportType === 'departing') {
    typeInProperTense = 'departures';
  } else if (exportType === 'arriving') {
    typeInProperTense = 'arrivals';
  }
  return `${categoryTypePrefixMapping[category]}${typeInProperTense}`;
};

/* eslint-disable no-underscore-dangle */
export const getReferenceDataValues = data =>
  data.map(datum => ((datum && datum.value) ? datum.value._id : ''));
/* eslint-enable no-underscore-dangle */

export const getCountryCodeValues = countries => countries.map(country => country && country.value);

export const getRegionsValues = regions =>
  regions.map(region => region && region.value && dataExportRegions[region.value]);

export const formatRequestData = (globalState, exportType) => {
  const {
    airlines,
    arrivalAirports,
    departureAirports,
    arrivalCountries,
    departureCountries,
    arrivalRegions,
    departureRegions,
    arrivalCheckedSearch,
    departureCheckedSearch,
    exportFormIncludeCodeshares,
    exportFormincludeCargo,
    exportFormStartDate,
    exportFormEndDate,
    userExportIntendedUse,
    userExportIndustrySelect,
    category,
  } = globalState && globalState[NAME];

  const locationQueries = {
    arriving: {
      airport: arrivalAirports &&
      arrivalAirports.length ? getReferenceDataValues(arrivalAirports) : null,
      countries: arrivalCountries &&
      arrivalCountries.length ? getCountryCodeValues(arrivalCountries) : null,
      regions: arrivalRegions &&
      arrivalRegions.length ? getRegionsValues(arrivalRegions) : null,
    },
    departing: {
      airport: departureAirports &&
      departureAirports.length ? getReferenceDataValues(departureAirports) : null,
      countries: departureCountries &&
      departureCountries.length ? getCountryCodeValues(departureCountries) : null,
      regions: departureRegions &&
      departureRegions.length ? getRegionsValues(departureRegions) : null,
    },
  };

  const validQueries = {
    includeCodeshares: exportFormIncludeCodeshares,
    includeCargo: exportFormincludeCargo,
  };
  if (airlines && airlines.length) {
    validQueries.airlines = getReferenceDataValues(airlines).join(',');
  }

  const places = formatPlacesQuery({
    exportType,
    departureCheckedSearch,
    arrivalCheckedSearch,
    locationQueries,
  });

  const type = getEnumTypesForExportTypes(category, exportType);

  const query = {
    fromYear: moment(exportFormStartDate).utc().year(),
    fromMonth: (moment(exportFormStartDate).utc().month() + 1),
    fromDay: moment(exportFormStartDate).utc().date(),
    toYear: moment(exportFormEndDate).utc().year(),
    toMonth: (moment(exportFormEndDate).utc().month() + 1),
    toDay: moment(exportFormEndDate).utc().date(),
    ...places,
    queries: validQueries,
  };

  return {
    metadata: {},
    query,
    type,
    userIntendedUse: userExportIntendedUse,
    userIndustry: userExportIndustrySelect,
  };
};

export const getExportForPolling = (previousDataExports, id) => previousDataExports &&
  previousDataExports.length &&
  previousDataExports.find(dataExport => dataExport.id === id);

export const translateEnumTypeToExportType = (enumType) => {
  let exportType = '';
  for (const key in categoryTypePrefixMapping) {
    if (has.call(categoryTypePrefixMapping, key)) {
      exportType = enumType.replace(categoryTypePrefixMapping[key], '');
    }
  }
  switch (exportType) {
    case 'arrivals':
      return 'arriving';
    case 'departures':
      return 'departing';
    default:
      return exportType;
  }
};

export const convertStringDateToMoment = (stringDate) => {
  if (typeof stringDate === 'string') {
    return moment(stringDate).format() !== 'Invalid date' ? moment.utc(moment(stringDate)) : moment.utc(moment());
  }
  return stringDate;
};

export const handleDateFieldValidation = (date) => {
  if (!date) {
    return STANDARD_ERROR;
  }
  if (date.isValid && date.isValid() && date.format() !== 'Invalid date' && !date.isBefore(DATA_EXPORTS_EARLIEST_DATE)) {
    return null;
  }
  return 'Invalid Date';
};

export const toDateIsNotBeforeFromDate = (values) => {
  const {
    toYear, toMonth, toDay, fromYear, fromMonth, fromDay,
  } = values;
  const toDate = moment.utc(moment().year(toYear).month(toMonth - 1).date(toDay));
  const fromDate = moment.utc(moment().year(fromYear).month(fromMonth - 1).date(fromDay));
  return toDate.isSameOrAfter(fromDate, 'day') ? true : 'Invalid Date';
};

export const handleTextInputValidation = (text) => {
  if (!text || typeof text !== 'string') {
    return STANDARD_ERROR;
  }
  // if (text.length < 3) {
  //   return 'Three Characters Minimum';
  // }
  return null;
};

export const handleSelectOptionValidation = option =>
  ((!option || option === '...Select') ? STANDARD_ERROR : null);

export const validationCallbacks = {
  date: handleDateFieldValidation,
  bool: () => false,
  text: handleTextInputValidation,
  select: handleSelectOptionValidation,
};

export const validateInputsOnFormSubmit = (state) => {
  const result = {
    invalidValues: [],
  };
  const values = Object.keys(inputObjectDefaults).map((key) => {
    let value = state && state[NAME] && state[NAME][inputObjectDefaults[key].name];
    if (inputObjectDefaults[key].type === 'date') {
      value = convertStringDateToMoment(value);
    }
    return Object.assign({}, inputObjectDefaults[key], { value });
  });

  const valid = values.every(input => validationCallbacks[input.type] && !validationCallbacks[input.type](input.value));
  if (!valid) {
    values.forEach((input) => {
      const { value, name, type } = input;
      result[name] = value;
      result[`${name}Invalid`] = validationCallbacks[type] && validationCallbacks[type](value);
      result.invalidValues.push(name);
    });
    result.invalidForm = true;
  }
  return result;
};

export const filterQuoteOnlyStatuses = (previousDataExports, currentlyGeneratingExportId) => {
  // add the new polling status export to the front if it exists
  const mostRecent = getExportForPolling(previousDataExports, currentlyGeneratingExportId);

  let dataExports = [];
  if (previousDataExports && previousDataExports.length) {
    dataExports = previousDataExports.filter((dataExport) => {
      const { status, id } = dataExport;
      return (status && previousDataExportScreenStatuses[status]) &&
        (id !== currentlyGeneratingExportId);
    });
  }
  if (mostRecent) {
    dataExports.unshift(mostRecent);
  }
  return dataExports;
};

export const isNonPollableState = previousDataExports => previousDataExports.every((dataExport) => {
  const { status } = dataExport;
  return !status || noPollForStatuses[status];
});

export const validateMultiSelectsOnFormSubmit = (state, exportType) => {
  const {
    arrivalCheckedSearch,
    departureCheckedSearch,
  } = state && state[NAME];

  const arrivalCheckName = referenceDataValidationMap[arrivalCheckedSearch][0];
  const arrivalToCheck = state && state[NAME] && state[NAME][arrivalCheckName];
  const departureCheckName = referenceDataValidationMap[departureCheckedSearch][1];
  const departureToCheck = state && state[NAME] && state[NAME][departureCheckName];
  const result = {
    invalidForm: null,
    arrivalCheckedSearchInvalid: null,
    departureCheckedSearchInvalid: null,
    invalidValues: [],
  };

  if (exportType && (exportType === 'route' || exportType === 'departing')) {
    if (!departureToCheck || !departureToCheck.length) {
      result.invalidValues.push(`${departureCheckName}-${exportType}`);
      result.invalidForm = true;
      result.departureCheckedSearchInvalid = STANDARD_ERROR;
    }
  }
  if (exportType && exportType !== 'departing') {
    if (!arrivalToCheck || !arrivalToCheck.length) {
      result.invalidValues.push(`${arrivalCheckName}-${exportType}`);
      result.invalidForm = true;
      result.arrivalCheckedSearchInvalid = STANDARD_ERROR;
    }
  }
  return result;
};

export const isOverDataLimit = action => !!action &&
  !!action.result &&
  !!action.result.data &&
  !!action.result.data.status &&
  action.result.data.status === 'over-data-limit';

export const isUnderDataMinimum = action => !!action &&
  !!action.result &&
  !!action.result.data &&
  !!action.result.data.status &&
  action.result.data.status === 'under-data-minimum';

export const parseCurrentQuoteResult = (result) => {
  if (result && result.data) {
    const {
      id,
      invalidForm,
      exportFormEndDateInvalid,
      exportFormStartDateInvalid,
    } = result.data;
    return {
      id,
      invalidForm,
      exportFormEndDateInvalid,
      exportFormStartDateInvalid,
    };
  }
  return { id: null };
};

export const isPollableState = (previousDataExports) => {
  if (previousDataExports && previousDataExports.length) {
    return !isNonPollableState(previousDataExports);
  }
  return false;
};



// WEBPACK FOOTER //
// ./modules/DataExports/redux/selectors.js